import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileUp } from "lucide-react";
import { defineModuleUi, postgrestSource, useOrg } from "@vixio/data";
import { DataTable, PageHeader, StatusFromMap, formatInt, lazyPage } from "@vixio/ui";
import "./api";
import { BATCH_STATUS, useTargets, type Batch } from "./api";

export * from "./api";

// Telas carregadas sob demanda: cada uma vira um arquivo à parte no build.
const NewImportPage = lazyPage(() => import("./NewImportPage"), "NewImportPage");
const BatchPage = lazyPage(() => import("./BatchPage"), "BatchPage");

function HistoryPage() {
  const org = useOrg();
  const navigate = useNavigate();
  const targets = useTargets().data ?? [];
  const label = new Map(targets.map((t) => [t.key, t.label]));
  const source = useMemo(() => postgrestSource<Batch>({ schema: "onboarding", table: "batches", orgId: org.id, searchColumns: ["filename", "sheet_name"], tiebreak: "id" }), [org.id]);
  return (
    <div className="vx-content">
      <PageHeader
        title="Importação de planilhas"
        description="Cinco passos: arquivo, mapeamento, validação, prévia e aplicar. Idempotente e com relatório de erros por linha."
        actions={<Link className="vx-btn vx-btn--primary" to="/configuracoes/importacao/nova">Nova importação</Link>}
      />
      <DataTable<Batch>
        caption="Histórico de importações"
        source={source}
        rowKey={(b) => b.id}
        onRowClick={(b) => navigate(`/configuracoes/importacao/${b.id}`)}
        initialSort={{ key: "created_at", dir: "desc" }}
        searchPlaceholder="Buscar arquivo ou aba"
        exportName="importacoes"
        filters={[
          { key: "status", label: "Situação", options: Object.entries(BATCH_STATUS).map(([value, s]) => ({ value, label: s.label })) },
          { key: "target_key", label: "Destino", options: targets.map((t) => ({ value: t.key, label: t.label })) },
        ]}
        empty="Nenhuma importação ainda."
        columns={[
          { key: "created_at", header: "Enviado", kind: "datetime", sortable: true },
          { key: "filename", header: "Arquivo", sortable: true, cell: (b) => <span className="vx-table__primary"><Link to={`/configuracoes/importacao/${b.id}`} onClick={(e) => e.stopPropagation()}>{b.filename}</Link>{b.sheet_name ? <span className="vx-table__sub">aba {b.sheet_name}</span> : null}</span> },
          { key: "target_key", header: "Destino", cell: (b) => label.get(b.target_key) ?? b.target_key, csv: (b) => label.get(b.target_key) ?? b.target_key },
          { key: "rows_total", header: "Linhas", kind: "number", sortable: true },
          { key: "created_rows", header: "Criadas", kind: "number" },
          { key: "skipped_rows", header: "Puladas", kind: "number" },
          { key: "errors", header: "Com erro", align: "right", cell: (b) => { const n = b.invalid_rows + b.failed_rows; return <span className={n ? "num text-crit" : "num muted"}>{formatInt(n)}</span>; }, csv: (b) => b.invalid_rows + b.failed_rows },
          { key: "status", header: "Situação", cell: (b) => <StatusFromMap map={BATCH_STATUS} value={b.status} />, csv: (b) => BATCH_STATUS[b.status]?.label ?? b.status },
        ]}
      />
    </div>
  );
}

export default defineModuleUi({
  key: "onboarding",
  title: "Importação",
  domain: "platform",
  icon: FileUp,
  menu: [{ path: "/configuracoes/importacao", label: "Importação", minRole: "member", icon: FileUp, order: 80, keywords: "planilha csv xlsx excel pm3 onboarding carga" }],
  routes: [
    { path: "/configuracoes/importacao", element: <HistoryPage />, minRole: "member" },
    { path: "/configuracoes/importacao/nova", element: <NewImportPage />, minRole: "member" },
    { path: "/configuracoes/importacao/:id", element: <BatchPage />, minRole: "member" },
  ],
});
